import { createApiClient, type ApiClient, type Middleware } from "@cntrl/api-client";
import type { TokenProvider } from "@cntrl/native";
import { API_URL } from "../../config/env.js";
import { orgLensMiddleware } from "../orgLens.js";
import { useDevImpersonation } from "../devImpersonation.js";

/** Fired when the API returns 401 on a NON-auth route (revoked/expired bearer) — the app redirects to /login. */
export type UnauthorizedHandler = () => void;

/**
 * Try to resume the session after a 401 — reissue via the cookie (see `sessionSync.ts`). Resolves the fresh
 * bearer, or null when the session is genuinely gone. `staleBearer` is the bearer the 401 was answered to.
 */
export type BearerRecovery = (staleBearer: string | null) => Promise<string | null>;

/**
 * Build the app's single API client over the TokenProvider. Two middlewares:
 *   • onRequest — inject `Authorization: Bearer <token>` from the provider (in-memory read).
 *   • onResponse — clear tokens + notify ONLY on an explicit 401: never on 5xx or a
 *     network error (the Antenna mass-logout bug). The `/auth/*` routes are exempt — login/reissue own
 *     their own 401 outcome (wrong password, no session) and must not trigger the global logout+redirect.
 *
 * With `recover` (web), a 401 first gets ONE reissue-and-retry: another tab of the same browser may simply
 * have rotated the shared session's bearer (`sessionSync.ts`). Only if that fails is it a real logout.
 */
export function createCoreApiClient(
  tokenProvider: TokenProvider,
  onUnauthorized: UnauthorizedHandler,
  recover?: BearerRecovery,
): ApiClient {
  const isAuthRoute = (url: string) => url.includes("/api/v1/auth/");
  // A pristine copy of each authenticated request, for the one retry — `fetch` consumes the original's body.
  const retryCopies = new WeakMap<Request, Request>();

  const authMiddleware: Middleware = {
    async onRequest({ request }) {
      const bearer = tokenProvider.getBearer();
      if (bearer) {
        request.headers.set("Authorization", `Bearer ${bearer}`);
        if (recover && !isAuthRoute(request.url)) retryCopies.set(request, request.clone());
      }
      return request;
    },
    async onResponse({ request, response }) {
      // clear-on-401-ONLY. A 5xx / network failure falls through untouched (no onError handler that clears).
      if (response.status !== 401 || isAuthRoute(request.url)) return response;

      const copy = retryCopies.get(request);
      retryCopies.delete(request);
      // An impersonated (dev switcher) session has no cookie of its own to resume — its 401 is final.
      if (recover && copy && !useDevImpersonation.getState().actingAs) {
        const stale = request.headers.get("Authorization")?.replace(/^Bearer /, "") ?? null;
        const fresh = await recover(stale);
        if (fresh) {
          copy.headers.set("Authorization", `Bearer ${fresh}`);
          try {
            const retried = await fetch(copy);
            if (retried.status !== 401) return retried;
          } catch {
            return response;   // network failure on the retry is not a session verdict — never logout on it
          }
        }
      }

      await tokenProvider.clear();
      onUnauthorized();
      return response;
    },
  };

  // Order: the org-lens rewrite runs FIRST (the shell picks the org prefix), then auth injects the
  // bearer onto the (possibly rewritten) request. The clone in orgLensMiddleware preserves headers either way.
  return createApiClient({ baseUrl: API_URL, middlewares: [orgLensMiddleware, authMiddleware] });
}
